import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";

const TRUNCATE_LENGTH = 200;
const COLS = 3;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PALETTE = [
  "#2563EB","#B45309","#7C3AED","#0F766E",
  "#BE185D","#1D4ED8","#065F46","#9333EA",
  "#C2410C","#0369A1",
];

function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function getInitials(name) {
  const p = name.trim().split(/\s+/);
  return p.length === 1 ? p[0].slice(0, 2).toUpperCase() : (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function useInView(threshold = 0.08) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function QuoteIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
    </svg>
  );
}

function StarSVG({ filled }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" stroke="none">
      <polygon
        points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
        fill={filled ? "#F59E0B" : "#CBD5E1"}
      />
    </svg>
  );
}

function HalfStarSVG() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" stroke="none">
      <defs>
        <linearGradient id="hg">
          <stop offset="50%" stopColor="#F59E0B" />
          <stop offset="50%" stopColor="#CBD5E1" />
        </linearGradient>
      </defs>
      <polygon
        points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
        fill="url(#hg)"
      />
    </svg>
  );
}

function Stars({ rating }) {
  return (
    <div className="flex items-center gap-[2px]">
      {Array.from({ length: 5 }, (_, i) => {
        if (i + 1 <= Math.floor(rating)) return <StarSVG key={i} filled />;
        if (i < rating) return <HalfStarSVG key={i} />;
        return <StarSVG key={i} />;
      })}
    </div>
  );
}

function ArrowRight() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

function TestimonialCard({ t, delay, inView, expanded, onToggle }) {
  const [hovered, setHovered] = useState(false);
  const color = avatarColor(t.name);
  const ini = getInitials(t.name);
  const isNeg = t.rating <= 2;
  const needsTrunc = t.quote.length > TRUNCATE_LENGTH;
  const display = needsTrunc && !expanded
    ? t.quote.slice(0, TRUNCATE_LENGTH).trimEnd() + "…"
    : t.quote;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
      className={`
        rounded-2xl border flex flex-col h-full cursor-default
        transition-all duration-300
        ${isNeg
          ? hovered
            ? "bg-red-900 border-red-400/30 shadow-[0_8px_32px_rgba(185,28,28,0.22)] -translate-y-0.5"
            : "bg-white-pure border-red-200/80 shadow-[0_2px_14px_rgba(185,28,28,0.06)]"
          : hovered
            ? "bg-blue-dark border-blue-primary/40 shadow-[0_8px_36px_rgba(0,45,181,0.22)] -translate-y-0.5"
            : "bg-white-pure border-blue-primary/15 shadow-[0_2px_14px_rgba(1,46,114,0.07)]"
        }
      `}
    >
      <div className="p-5 flex flex-col h-full">

        {/* Quote icon + stars */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className={`
            w-9 h-9 rounded-full border flex items-center justify-center flex-shrink-0
            transition-all duration-300
            ${isNeg
              ? hovered ? "border-red-300/25 text-red-200/50" : "border-red-200 text-red-400/60 bg-red-50"
              : hovered ? "border-white-pure/20 text-white-pure/40" : "border-blue-primary/20 text-blue-primary/50 bg-blue-primary/5"
            }
          `}>
            <QuoteIcon />
          </div>
          <Stars rating={t.rating} />
        </div>

        {/* Quote text */}
        <div className="flex-1 flex flex-col">
          <p className={`
            font-constantia italic text-[0.875rem] leading-[1.85]
            transition-colors duration-300
            ${isNeg
              ? hovered ? "text-red-100/85" : "text-red-900/72"
              : hovered ? "text-white-pure/82" : "text-blue-dark/70"
            }
          `}>
            "{display}"
          </p>

          {needsTrunc && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggle(); }}
              className={`
                self-start font-cinzel font-bold text-[0.62rem] tracking-[0.08em] uppercase
                mt-2 transition-opacity duration-200 hover:opacity-60
                ${isNeg
                  ? hovered ? "text-red-300" : "text-red-500"
                  : hovered ? "text-white-pure/60" : "text-blue-primary"
                }
              `}
            >
              {expanded ? "Read less ↑" : "Read more ↓"}
            </button>
          )}
        </div>

        <div className="mt-4" />

        {/* Divider */}
        <div className={`h-px mb-4 flex-shrink-0 transition-colors duration-300 ${
          isNeg
            ? hovered ? "bg-gradient-to-r from-transparent via-red-300/20 to-transparent" : "bg-gradient-to-r from-transparent via-red-200 to-transparent"
            : hovered ? "bg-gradient-to-r from-transparent via-white-pure/15 to-transparent" : "bg-gradient-to-r from-transparent via-blue-dark/10 to-transparent"
        }`} />

        {/* Author row */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-shadow duration-300"
            style={{
              backgroundColor: color,
              boxShadow: hovered ? `0 0 14px ${color}90` : "none",
            }}
          >
            <span className="font-cinzel font-bold text-[0.58rem] text-white-pure tracking-wider">
              {ini}
            </span>
          </div>

          <span className={`
            font-cinzel font-bold text-[0.7rem] tracking-[0.03em] flex-1 min-w-0 truncate
            transition-colors duration-300
            ${isNeg
              ? hovered ? "text-white-pure" : "text-red-900"
              : hovered ? "text-white-pure" : "text-blue-dark"
            }
          `}>
            {t.name}
          </span>

          <span className={`
            font-constantia text-[0.65rem] whitespace-nowrap px-2.5 py-[3px] rounded-full border
            flex-shrink-0 transition-all duration-300
            ${isNeg
              ? hovered ? "text-red-200/45 border-red-300/20" : "text-red-400/55 border-red-200 bg-red-50"
              : hovered ? "text-white-pure/35 border-white-pure/15" : "text-blue-dark/40 border-blue-dark/10 bg-blue-primary/5"
            }
          `}>
            {t.result}
          </span>
        </div>

      </div>
    </div>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function HeroSection({ overallRating, totalReviews }) {
  const [ref, inView] = useInView(0.1);

  return (
    <section className="relative bg-blue-dark overflow-hidden py-20 sm:py-28 lg:py-32">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(ellipse 80% 70% at 50% 0%, rgba(0,45,181,0.4) 0%, transparent 70%),
            radial-gradient(ellipse 50% 45% at 100% 100%, rgba(0,20,90,0.35) 0%, transparent 60%)
          `,
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: "200px 200px",
        }}
      />

      <div
        ref={ref}
        className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center"
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? "translateY(0)" : "translateY(24px)",
          transition: "opacity 0.85s ease, transform 0.85s ease",
        }}
      >
        <div className="inline-flex items-center gap-2 mb-7">
          <div className="w-6 h-px bg-white-pure/30" />
          <span className="font-cinzel font-bold text-[0.58rem] tracking-[0.3em] uppercase text-white-pure/55 border border-white-pure/18 px-4 py-[5px] rounded-full">
            CLIENT STORIES
          </span>
          <div className="w-6 h-px bg-white-pure/30" />
        </div>

        <h1
          className="font-cinzel font-black text-white-pure tracking-[0.04em] leading-[1.1] mb-6"
          style={{
            fontSize: "clamp(1.55rem, 4.5vw, 3.25rem)",
            textShadow: "0 2px 48px rgba(0,45,181,0.55)",
          }}
        >
          TESTIMONIALS FROM<br className="hidden sm:block" /> BUSINESSES WE PROTECT
        </h1>

        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-12 h-px bg-gradient-to-r from-transparent to-white-pure/30" />
          <div className="w-1.5 h-1.5 rounded-full bg-white-pure/40 border border-white-pure/55" />
          <div className="w-12 h-px bg-gradient-to-l from-transparent to-white-pure/30" />
        </div>

        <p
          className="font-constantia italic text-white-pure/55 leading-[1.85] mb-10 max-w-2xl mx-auto"
          style={{ fontSize: "clamp(0.88rem, 1.6vw, 1.05rem)" }}
        >
          Every account is personal to us. Here is what Paladin clients say about the way we guide coverage decisions, communicate clearly, and stay dependable year-round.
        </p>

        <div className="inline-flex items-center gap-3 border border-white-pure/20 bg-white-pure/[0.06] backdrop-blur-sm rounded-full px-6 py-3">
          <span className="font-cinzel font-black text-white-pure text-xl tracking-tight">
            {overallRating}
          </span>
          <Stars rating={overallRating} />
          <span className="font-constantia italic text-white-pure/50 text-sm">
            {totalReviews} {totalReviews === 1 ? "review" : "reviews"}
          </span>
        </div>
      </div>
    </section>
  );
}

// ─── Cards Grid ───────────────────────────────────────────────────────────────

function CardsSection({ testimonials }) {
  const [ref, inView] = useInView(0.04);
  const [expandedRows, setExpandedRows] = useState({});

  const rows = [];
  for (let i = 0; i < testimonials.length; i += COLS) {
    rows.push(testimonials.slice(i, i + COLS));
  }

  const toggleRow = (rowIdx) => {
    setExpandedRows((prev) => ({ ...prev, [rowIdx]: !prev[rowIdx] }));
  };

  return (
    <section
      className="relative py-16 sm:py-20 lg:py-24"
      style={{ background: "linear-gradient(160deg,#EEF2F8 0%,#E8EDF6 50%,#EDF1F9 100%)" }}
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-primary/20 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-5">
        {rows.map((row, rowIdx) => {
          const isExpanded = !!expandedRows[rowIdx];

          return (
            <div
              key={rowIdx}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {row.map((t, colIdx) => {
                const globalIdx = rowIdx * COLS + colIdx;
                return (
                  <TestimonialCard
                    key={t.name + globalIdx}
                    t={t}
                    delay={Math.min(globalIdx * 60, 500)}
                    inView={inView}
                    expanded={isExpanded}
                    onToggle={() => toggleRow(rowIdx)}
                  />
                );
              })}

              {row.length < COLS &&
                Array.from({ length: COLS - row.length }).map((_, i) => (
                  <div key={"empty-" + i} className="hidden lg:block" />
                ))
              }
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── CTA Section ─────────────────────────────────────────────────────────────

function CTASection() {
  const navigate = useNavigate();
  const [ref, inView] = useInView(0.12);

  return (
    <section className="relative overflow-hidden bg-blue-dark py-24 sm:py-28">

      {/* Radial glows */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(ellipse 65% 70% at 50% 110%, rgba(0,45,181,0.45) 0%, transparent 65%),
            radial-gradient(ellipse 50% 40% at 0% 0%, rgba(0,20,90,0.35) 0%, transparent 55%),
            radial-gradient(ellipse 40% 35% at 100% 0%, rgba(0,45,181,0.2) 0%, transparent 55%)
          `,
        }}
      />

      {/* Grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.032]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n2'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n2)'/%3E%3C/svg%3E")`,
          backgroundSize: "200px 200px",
        }}
      />

      {/* Grid lines */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.018) 1px,transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      {/* Ghost concentric circles ornament */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none rounded-full"
        style={{
          width: "clamp(260px, 38vw, 520px)",
          height: "clamp(260px, 38vw, 520px)",
          border: "1px solid rgba(255,255,255,0.03)",
          boxShadow: "0 0 0 30px rgba(255,255,255,0.015), 0 0 0 70px rgba(255,255,255,0.008)",
        }}
      />

      {/* Content */}
      <div
        ref={ref}
        className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 text-center"
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? "translateY(0)" : "translateY(28px)",
          transition: "opacity 0.8s ease, transform 0.8s ease",
        }}
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-2 mb-8">
          <div className="w-6 h-px bg-white-pure/30" />
          <span className="font-cinzel font-bold text-[0.58rem] tracking-[0.28em] uppercase text-white-pure/50 border border-white-pure/16 px-4 py-[5px] rounded-full">
            Get Started
          </span>
          <div className="w-6 h-px bg-white-pure/30" />
        </div>

        {/* Headline */}
        <h2
          className="font-cinzel font-black text-white-pure leading-[1.12] tracking-[0.03em] mb-5"
          style={{
            fontSize: "clamp(1.6rem, 3.8vw, 2.75rem)",
            textShadow: "0 2px 40px rgba(0,45,181,0.5)",
          }}
        >
          READY FOR A BETTER<br className="hidden sm:block" /> INSURANCE EXPERIENCE?
        </h2>

        {/* Decorative rule */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-10 h-px bg-gradient-to-r from-transparent to-white-pure/28" />
          <div className="w-[5px] h-[5px] rounded-full bg-white-pure/35 border border-white-pure/50" />
          <div className="w-10 h-px bg-gradient-to-l from-transparent to-white-pure/28" />
        </div>

        {/* Subtitle */}
        <p
          className="font-constantia italic text-white-pure/52 leading-[1.85] mb-10 max-w-xl mx-auto"
          style={{ fontSize: "clamp(0.9rem, 1.5vw, 1.05rem)" }}
        >
          We would love to learn about your goals and build a coverage strategy that fits your business, team, and long-term plans.
        </p>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => navigate("/quote")}
            className="
              font-cinzel font-bold text-[0.72rem] tracking-[0.12em] uppercase
              inline-flex items-center gap-2 rounded-full px-8 py-[13px] cursor-pointer
              transition-all duration-300
              bg-white-pure text-blue-dark border border-white-pure
              hover:bg-blue-primary hover:text-white-pure hover:border-blue-primary
              hover:shadow-[0_8px_32px_rgba(0,45,181,0.4)]
            "
          >
            Request A Quote
            <ArrowRight />
          </button>

          <button
            onClick={() => navigate("/contact")}
            className="
              font-cinzel font-bold text-[0.72rem] tracking-[0.12em] uppercase
              inline-flex items-center gap-2 rounded-full px-8 py-[13px] cursor-pointer
              transition-all duration-300
              bg-transparent text-white-pure border border-white-pure/35
              hover:bg-white-pure hover:text-blue-dark hover:border-white-pure
              hover:shadow-[0_8px_32px_rgba(0,0,0,0.25)]
            "
          >
            Contact Our Team
            <ArrowRight />
          </button>
        </div>

        {/* Trust line */}
        <p className="mt-8 font-constantia italic text-white-pure/28 text-[0.75rem] tracking-wide">
          No obligation · Quick response · Personalized coverage
        </p>
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TestimonialPage() {
  usePageTitle("Testimonials");
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/testimonials`)
      .then((r) => r.json())
      .then(({ data }) => {
        const mapped = (data || []).map((t) => ({
          ...t,
          quote: t.text,
          result: t.timeLabel,
        }));
        setTestimonials(mapped);
      })
      .catch(console.error);
  }, []);

  const overallRating =
    testimonials.length
      ? +(testimonials.reduce((s, t) => s + t.rating, 0) / testimonials.length).toFixed(1)
      : 0;

  return (
    <main className="min-h-screen w-full">
      <HeroSection overallRating={overallRating} totalReviews={testimonials.length} />
      <CardsSection testimonials={testimonials} />
      <CTASection />
    </main>
  );
}